import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { type Id } from '@/infra/id'
import type { Document } from '@/features/docs/Document'
import type {
  DocumentStore,
  NewDocumentAccessOptions,
} from '@/features/docs/DocumentStore'
import { type DocumentsListViewModel } from '@/features/docs/DocumentsListViewModel'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { DocumentsListViewModelFactory } from '../..'
import { match } from '@/utils/pattern-match-util'
import { action } from 'mobx'
import { tapAction } from '@/utils/mobx-util'

/**
 * Context for linking documents.
 */
export type DocumentLinkingViewModelQueryContext =
  JobDocumentLinkingViewModelQueryContext

/**
 * Context for linking documents to a job.
 */
export interface JobDocumentLinkingViewModelQueryContext {
  type: 'Job'
  jobId: Id
}

/**
 * Options for the {@link DocumentLinkingViewModel}.
 */
export interface DocumentLinkingViewModelOptions {
  /**
   * Optional. If provided, will be used to filter documents.
   */
  filter?: (doc: Document) => boolean

  /**
   * The context of the document linking.
   */
  context: DocumentLinkingViewModelQueryContext

  /**
   * Optional. If provided, will be called when document linking is completed.
   */
  onLinkingCompleted?: (doc: Document) => Promise<unknown>
}

/**
 * View model for linking existing documents to a job.
 */
export class DocumentLinkingViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Modal view model for dialog state.
   */
  readonly modal = new ModalViewModel()

  /**
   * The view model for listing documents.
   */
  readonly documentsList: DocumentsListViewModel

  constructor(
    private readonly _options: DocumentLinkingViewModelOptions,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _documentStore: DocumentStore,
    createDocumentListViewModel: DocumentsListViewModelFactory,
  ) {
    this.documentsList = createDocumentListViewModel({
      onAction: this._linkDocument.bind(this),
      filter: this._options.filter,
      context: {
        type: 'User',
      },
    })

    this._lifecycle.manage(this.documentsList)

    // Clear the search text when the dialog closes.
    this._lifecycle.manage(
      this.modal.close$.pipe(
        tapAction(() => this.documentsList.searchText.clear()),
      ),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Shows the document linking dialog.
   */
  @action.bound
  show() {
    this.modal.show()
    void this.documentsList.fetch(this._lifecycle.signal)
  }

  /**
   * Converts the link context to document access options.
   */
  private _contextToAccessOptions(
    context: DocumentLinkingViewModelQueryContext,
  ): NewDocumentAccessOptions {
    return match(context, 'type', {
      Job: (ctx) => ({
        type: 'Job' as const,
        jobId: ctx.jobId,
      }),
    })
  }

  /**
   * Links a document to the job.
   */
  private async _linkDocument(doc: Document) {
    if (!doc.id) {
      return
    }

    if (!this._options.filter?.(doc)) {
      return
    }

    const errorHandler = this._createSnackbarErrorHandler(
      'Error linking document',
    )
    try {
      const accessOptions = this._contextToAccessOptions(this._options.context)
      await this._documentStore.createDocumentAccess(doc.longId, accessOptions)

      if (this._options.onLinkingCompleted) {
        await this._options.onLinkingCompleted(doc)
      }

      this.modal.close()
    } catch (error) {
      errorHandler(error)
    }
  }
}
